import React from 'react';
import { Link } from 'react-router-dom';
import { LockIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../ui/Field';
import { submitLead } from '../../lib/leads';
import { track } from '../../lib/analytics';
import type { Article } from '../../data/insights';
interface SoftGateProps {
  article: Article;
  onUnlock: () => void;
}
export function SoftGate({
  article,
  onUnlock
}: SoftGateProps) {
  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  React.useEffect(() => {
    track('article_gate_view', {
      source: article.slug,
      line: article.serviceLine
    });
  }, [article.slug, article.serviceLine]);
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName,
      organisation,
      consent,
      source: `article:${article.slug}`,
      line: article.serviceLine,
      sector: article.sectors[0]
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    onUnlock();
  };
  return <div className="relative -mt-40" data-id="element-1303" data-mp="2b5xa8s4n6i26">
      <div className="gate-fade pointer-events-none h-40 w-full" aria-hidden="true" data-id="element-1304" data-mp="2g61n886ccunn" />
      <div className="rounded-md border border-line bg-champagne p-6 sm:p-8" data-id="element-1305" data-mp="2l5eauv51e5ra">
        <div className="flex items-center gap-2 text-gold" data-id="element-1306" data-mp="2pwo03umob72n">
          <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1307" data-mp="2py7jhu3mly8n" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-1308" data-mp="1si20rkg2bqev">Free to read</span>
        </div>
        <h2 className="mt-4 font-display text-[24px] leading-tight text-ink sm:text-[28px]" data-id="element-1309" data-mp="1xfbnutfzbpzj">
          {article.gateHeadline}
        </h2>
        <p className="mt-3 max-w-prose text-[15px] text-body" data-id="element-1310" data-mp="1pdkqa8r69zir">{article.gateValueLine}</p>

        <form onSubmit={onSubmit} className="mt-7 space-y-5" data-id="element-1311" data-mp="1kgav0ch3qtn0">
          <div className="grid gap-5 sm:grid-cols-2" data-id="element-1312" data-mp="1z5qlrsgmuz2p">
            <Field id="gate-email" label="Work email" required error={error ?? undefined} data-id="element-1313" data-mp="1z7bc15vwzvur">
              <input id="gate-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" aria-describedby={error ? 'gate-email-error' : undefined} className={inputClass} data-id="element-1314" data-mp="1u8gtm51v09b6" />
            </Field>
            <Field id="gate-name" label="First name" data-id="element-1315" data-mp="1bi8z2bc86r6l">
              <input id="gate-name" type="text" value={firstName} onChange={event => setFirstName(event.target.value)} className={inputClass} data-id="element-1316" data-mp="2rt32hm044co3" />
            </Field>
          </div>
          <Field id="gate-org" label="Organisation" data-id="element-1317" data-mp="2ze6ps9lh337q">
            <input id="gate-org" type="text" value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-1318" data-mp="2zeyj1drlkouu" />
          </Field>

          <ConsentCheckbox id="gate-consent" checked={consent} onChange={setConsent} data-id="element-1319" data-mp="3d4k02ilu6u08">
            I am happy for Cavalla Solution to email me insights and occasional updates. We handle your data in
            line with our{' '}
            <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-1320" data-mp="34byp3oog5rly">
              privacy notice
            </Link>{' '}
            and you can unsubscribe at any time.
          </ConsentCheckbox>

          <Button type="submit" size="lg" disabled={submitting} data-id="element-1321" data-mp="3w2ms769ol729">
            {submitting ? 'Unlocking' : 'Continue reading'}
          </Button>
        </form>
      </div>
    </div>;
}